import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import {
  ColorDot, Body, ProgressBar, Theme, Tooltip, IconType, Icon,
} from '@routific/hive';
import { useTranslation } from 'react-i18next';
import TooltipTable from '../../../shared/TooltipTable';
import { useStore } from '../../../../store/mst';

const TOTAL_PERCENT = 100;
interface Props {
  totalOrders: number;
  totalSkipped?: number | null;
  totalCompleted?: number | null;
  published?: boolean;
  percentProgress: number;
  delay?: number;
  routeUuid?: string;
}

const Container = styled.span`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTooltip = styled(Tooltip)`
  align-items: center;
  width: 100%;
`;

const StyledLabel = styled.div<{ $delay?: number }>`
  white-space: nowrap;
  ${({ $delay }) => (
    $delay && $delay > 10 ?
      css`color: ${Theme.Colors.warning4};` :
      css`color: ${Theme.Colors.primary7};`)}
  `;

const getKeyElement = (text: string, color: string) => (
  <Container style={{ paddingRight: '40px' }}>
    <span style={{ paddingTop: 1 }}>
      <ColorDot color={color} />
    </span>
    <Body onDark>
      {` ${text}`}
    </Body>
  </Container>
);

const getValueElement = (value: number) => (
  <Body onDark>
    {' '}
    {value}
  </Body>
);

const getTooltipElements = (tooltipItems: { color: string, value: number, text: string }[]) => tooltipItems.map((item, index) => ({
  index: index.toString(),
  key: getKeyElement(item.text, item.color),
  value: getValueElement(item.value),
}));

const getTooltipTable = (rows: any) => <TooltipTable rows={getTooltipElements(rows)} />;
const renderIcon = (percentProgress: number) => {
  if (percentProgress === 100) {
    return IconType.Check;
  }

  return IconType.RouteLive;
};

function calculateOrderPercentage(totalCompleted: number, totalSkipped: number, totalOrders: number): { percentDelivered: number, percentSkipped: number } {
  if (totalOrders === 0) {
    return { percentDelivered: 0, percentSkipped: 0 };
  }

  const percentDelivered = (totalCompleted / totalOrders) * TOTAL_PERCENT;
  const percentSkipped = (totalSkipped / totalOrders) * TOTAL_PERCENT;

  return {
    percentDelivered,
    percentSkipped,
  };
}

const RouteSummaryProgressBar: React.FC<Props> = ({
  totalOrders,
  percentProgress,
  published,
  delay,
  routeUuid,
}) => {
  const { t } = useTranslation('components');
  const store = useStore();

  const progress = store.model.orderDeliveryProgressDtos.get(routeUuid!);
  const totalOrdersCompleted = progress?.delivered || 0;
  const totalOrdersSkipped = progress?.missed || 0;
  const totalOrdersRemaining = totalOrders - totalOrdersCompleted - totalOrdersSkipped || 0;

  const tooltipItems = [
    { color: Theme.Colors.primary4, value: totalOrdersCompleted, text: 'Delivered' },
    { color: Theme.Colors.warning6, value: totalOrdersSkipped, text: 'Missed' },
    { color: Theme.Colors.gray0, value: totalOrdersRemaining, text: 'Out for delivery' },
  ];
  const { percentDelivered, percentSkipped } = calculateOrderPercentage(totalOrdersCompleted, totalOrdersSkipped, totalOrders);


  if (!published && !store.ui.selectedWorkspace?.isSynced) {
    return null;
  }

  const ON_TIME_THRESHOLD_MINUTES = 10;
  let status = '';
  if (delay && delay > ON_TIME_THRESHOLD_MINUTES) {
    status = t('routeSummaryProgressBar.tagLabels.minutesLate', { minutes: delay });
  } else if (delay && delay < -ON_TIME_THRESHOLD_MINUTES) {
    status = t('routeSummaryProgressBar.tagLabels.minutesEarly', { minutes: Math.abs(delay) });
  } else {
    status = t('routeSummaryProgressBar.tagLabels.onTime');
  }

  return (
    <StyledTooltip
      title={getTooltipTable(tooltipItems)}
      color={Theme.Colors.textPrimary}
      overlayClassName="route-summary-tooltip"
      placement="bottom"
    >
      <Container>
        <ProgressBar
          percent={percentDelivered}
          percentComplete={percentSkipped}
          icon={<Icon icon={renderIcon(percentProgress)} />}
        />
        {(percentProgress === 100
          && (
            <Body type="secondary" style={{ whiteSpace: 'nowrap' }}>
              {status}
            </Body>
          ))}
        {(percentProgress !== 100 && (percentProgress !== 0 || (delay && Math.abs(delay) > ON_TIME_THRESHOLD_MINUTES))) && (
          <StyledLabel $delay={delay}>
            {status}
          </StyledLabel>
        )}
      </Container>
    </StyledTooltip>
  );
};

export default observer(RouteSummaryProgressBar);
